import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { downloadSubscriptionInvoice } from "../../utils/subscriptionInvoicePdf";
import { format } from "date-fns";
import {
  SA,
  SaPageHeader,
  saCardSx,
  saTableBodySx,
  saTableHeadSx,
  saToggleSx,
} from "../../components/superadmin/saUi";
import {
  PLATFORM_KEYS,
  PLATFORM_LABELS,
  PaymentsPage,
  PlatformKey,
  SubscriptionPaymentRow,
  listSubscriptionPayments,
} from "../../services/superadminService";
import {
  PaymentStatusChip,
  PlatformChip,
  formatMoney,
  invoiceCustomerName,
} from "../../components/superadmin/SubscriptionChips";

type StatusFilter = "ALL" | SubscriptionPaymentRow["status"];
const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "ALL", label: "All" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
];

const formatDay = (value: string | null) =>
  value ? format(new Date(value), "d MMM yyyy") : "—";

/**
 * Every Whish payment across all organizations. Each one is also that
 * organization's invoice, so this is the platform's billing ledger.
 */
const Payments: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as StatusFilter) || "ALL";
  const platform = (params.get("platform") as PlatformKey | null) || null;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState<PaymentsPage | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(
        await listSubscriptionPayments({
          ...(status !== "ALL" && { status }),
          ...(platform && { platform }),
          page: page + 1,
          pageSize,
        })
      );
    } catch {
      // The interceptor already surfaced the message.
    }
    setLoading(false);
  }, [status, platform, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key: "status" | "platform", value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(0);
  };

  return (
    <Box>
      <SaPageHeader
        title="Payments"
        subtitle="Whish payments for packages. Each payment is also the organization's invoice."
        icon={<PaymentsOutlinedIcon />}
        tone="green"
        actions={
          <Tooltip title="Refresh">
            <IconButton onClick={load} aria-label="Refresh payments">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={status}
            onChange={(_, value) => value && setFilter("status", value === "ALL" ? null : value)}
            sx={saToggleSx}
          >
            {STATUS_TABS.map((tab) => (
              <ToggleButton key={tab.value} value={tab.value} sx={{ whiteSpace: "nowrap" }}>
                {tab.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={platform ?? "ALL"}
          onChange={(_, value) => value && setFilter("platform", value === "ALL" ? null : value)}
          sx={saToggleSx}
        >
          <ToggleButton value="ALL">Both platforms</ToggleButton>
          {PLATFORM_KEYS.map((key) => (
            <ToggleButton key={key} value={key}>
              {PLATFORM_LABELS[key]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...saCardSx,
          overflow: "hidden",
          opacity: loading && data ? 0.6 : 1,
          transition: "opacity 150ms",
        }}
      >
        {!data ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            {loading ? <CircularProgress size={28} /> : <Typography>Could not load payments.</Typography>}
          </Box>
        ) : data.payments.length === 0 ? (
          <Typography sx={{ p: 3, color: SA.textMuted }}>No payments match these filters.</Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={saTableHeadSx}>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Package</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Invoice PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={saTableBodySx}>
                {data.payments.map((payment) => (
                  <TableRow
                    key={payment.id}
                    hover
                    onClick={() =>
                      payment.organization?.id &&
                      navigate(`/superadmin/organizations/${payment.organization.id}`)
                    }
                    sx={{ cursor: payment.organization?.id ? "pointer" : "default" }}
                  >
                    <TableCell sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                      #{payment.invoiceNumber}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "0.88rem",
                            // Deleted organizations stand out in red (same red as "Expired").
                            color: payment.organization?.deleted ? "#A32B22" : undefined,
                          }}
                          noWrap
                        >
                          {payment.organization?.name ?? "—"}
                        </Typography>
                        {payment.organization && <PlatformChip platform={payment.organization.platform} />}
                      </Box>
                    </TableCell>
                    <TableCell>{payment.planName}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                      {formatMoney(payment.amount, payment.currency)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={payment.failureReason ?? ""}>
                        <span>
                          <PaymentStatusChip status={payment.status} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {payment.periodStartsAt
                        ? `${formatDay(payment.periodStartsAt)} → ${formatDay(payment.periodEndsAt)}`
                        : `${payment.durationDays} days`}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {format(new Date(payment.paidAt ?? payment.createdAt), "d MMM yyyy, HH:mm")}
                    </TableCell>
                    <TableCell align="right">
                      {payment.status === "PAID" && (
                        <Tooltip title={`Download invoice #${payment.invoiceNumber}`}>
                          <IconButton
                            size="small"
                            aria-label={`Download invoice #${payment.invoiceNumber}`}
                            onClick={(e) => {
                              // The row itself opens the organization.
                              e.stopPropagation();
                              downloadSubscriptionInvoice(payment, invoiceCustomerName(payment));
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {data && data.total > 0 && (
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        )}
      </Paper>
    </Box>
  );
};

export default Payments;
